import React from "react";

import type { CoaSelectState } from "./CoaSelectPrompt";
import { coaStateRequiresUserInput } from "./CoaSelectPrompt";

export type UseCoaAutoFireParams = {
    /** Surface preconditions: RR-disabled flow, extraction idle, no error. */
    enabled: boolean;
    /** The file the extraction targets; the auto-fire runs once per file. */
    fileId: number | null;
    coaState: CoaSelectState;
    coaBlocksContinue: boolean;
    assignCoaThenRun: (
        run: (effectiveProjectId: number) => void,
    ) => Promise<boolean>;
    onFire: (effectiveProjectId: number) => void;
};

export type UseCoaAutoFireResult = {
    /**
     * The CoA gate screen must be shown instead of auto-firing: a failed check
     * needs a Retry, several permitted CoAs need a choice, or a silent assign
     * failed and the user must explicitly retry it.
     */
    coaGateNeeded: boolean;
};

/**
 * Auto-fires the OS extraction for flows with no document-type step (Rent Roll
 * upload disabled), restoring the pre-OS-3938 behaviour whenever the user has
 * nothing to decide: once the CoA checks settle to "no prompt needed" (already
 * assigned, locked, or a sole permitted CoA — assigned silently), the
 * extraction fires without an extra screen. Only an error, a required choice,
 * or a failed silent assign surface the gate screen (whose Continue retries via
 * the same `assignCoaThenRun`).
 */
export const useCoaAutoFire = (
    params: UseCoaAutoFireParams,
): UseCoaAutoFireResult => {
    const {
        enabled,
        fileId,
        coaState,
        coaBlocksContinue,
        assignCoaThenRun,
        onFire,
    } = params;

    // A silent sole-CoA assign failed: hand over to the gate screen so the user
    // retries explicitly instead of this effect looping failed PUTs. Cleared
    // when the target file changes; a lock discovered by the failure's refetch
    // flips the state to not_applicable, where auto-fire (without an assign)
    // resumes.
    const [autoAssignFailed, setAutoAssignFailed] = React.useState(false);
    React.useEffect(() => {
        setAutoAssignFailed(false);
    }, [fileId]);

    const coaGateNeeded =
        coaStateRequiresUserInput(coaState) ||
        (autoAssignFailed && coaState.kind === "ready");

    const firedFileIdRef = React.useRef<number | null>(null);
    React.useEffect(() => {
        if (!enabled || fileId === null) return;
        if (coaBlocksContinue || coaGateNeeded) return;
        if (firedFileIdRef.current === fileId) return;
        firedFileIdRef.current = fileId;
        void assignCoaThenRun(onFire).then(ran => {
            if (!ran) {
                firedFileIdRef.current = null;
                setAutoAssignFailed(true);
            }
        });
    }, [
        enabled,
        fileId,
        coaBlocksContinue,
        coaGateNeeded,
        assignCoaThenRun,
        onFire,
    ]);

    return { coaGateNeeded };
};
